"use client";

import { CalendarBlank, Clock, DownloadSimple, GraduationCap, Users } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { getPublicCourseDetail } from "@/api";
import CourseStructuredData from "@/app/components/SEO/CourseStructuredData";

const normalizeCoursePayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (payload.course && typeof payload.course === "object") return payload.course;
  if (payload.data && typeof payload.data === "object") return payload.data;
  return payload;
};

const splitFeatures = (value) =>
  String(value || "")
    .split("\n")
    .map((item) => item.replace(/^-\s*/, "").trim())
    .filter(Boolean);

const getDisplayPrice = (course) => {
  if (!course) return null;
  if (course.is_discounted && course.discount_price) return Number(course.discount_price);
  if (course.price) return Number(course.price);
  return null;
};

const CourseDetailClient = ({ courseId }) => {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const payload = await getPublicCourseDetail(courseId);
        const normalized = normalizeCoursePayload(payload);
        if (!cancelled) setCourse(normalized);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || "Failed to load course.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (courseId) load();

    return () => {
      cancelled = true;
    };
  }, [courseId]);

  const features = useMemo(() => splitFeatures(course?.essential_features), [course?.essential_features]);
  const displayPrice = useMemo(() => getDisplayPrice(course), [course]);
  const curriculumSections = useMemo(() => {
    if (!Array.isArray(course?.course_content)) return [];
    return course.course_content.filter((section) => Array.isArray(section?.items) && section.items.length > 0);
  }, [course?.course_content]);
  const curriculumDocuments = useMemo(
    () => (Array.isArray(course?.curriculum_documents) ? course.curriculum_documents.filter((item) => item?.path) : []),
    [course?.curriculum_documents]
  );
  const batchDetails = useMemo(
    () =>
      [
        course?.starting_date
          ? {
              icon: CalendarBlank,
              label: "Starting date",
              value: new Date(course.starting_date).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              }),
            }
          : null,
        course?.batch_time
          ? {
              icon: Clock,
              label: "Batch time",
              value: course.batch_time,
            }
          : null,
        course?.batch_type || (Array.isArray(course?.batch_days) && course.batch_days.length > 0)
          ? {
              icon: Users,
              label: "Batch schedule",
              value: [course?.batch_type, Array.isArray(course?.batch_days) ? course.batch_days.join(", ") : ""]
                .filter(Boolean)
                .join(" • "),
            }
          : null,
        course?.few_seats_left
          ? {
              icon: GraduationCap,
              label: "Availability",
              value: "Few seats left",
            }
          : null,
      ].filter(Boolean),
    [course]
  );

  if (loading) {
    return (
      <section className="w-full py-24 px-4 lg:px-20 text-center">
        <p className="text-gray-600">Loading course details...</p>
      </section>
    );
  }

  if (error || !course) {
    return (
      <section className="w-full py-24 px-4 lg:px-20 text-center">
        <p className="text-red-600">{error || "Course not found."}</p>
        <Link href="/" className="inline-block mt-4 text-primary hover:underline">
          Back to home
        </Link>
      </section>
    );
  }

  return (
    <section className="w-full py-14 lg:py-20 px-4 lg:px-20 bg-[#f7fbff]">
      <CourseStructuredData course={course} />
      <div className="max-w-6xl mx-auto">
        <nav className="text-sm text-gray-500 mb-6">
          <Link href="/" className="hover:text-primary">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-700">{course.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 bg-white border border-gray-100 rounded-2xl p-6 lg:p-8 shadow-sm">
          <div>
            {course.cover_image ? (
              <div className="relative w-full h-[280px] lg:h-[360px] rounded-xl overflow-hidden bg-[#f5f7fb]">
                <Image
                  src={course.cover_image}
                  alt={course.title}
                  fill
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  className="object-contain"
                  priority
                />
              </div>
            ) : (
              <div className="w-full h-[280px] lg:h-[360px] rounded-xl bg-gray-100 flex items-center justify-center text-gray-500">
                No image available
              </div>
            )}
          </div>

          <div className="flex flex-col">
            <h1 className="text-3xl lg:text-4xl font-semibold text-[#1A1A1A] mb-4">{course.title}</h1>
            {displayPrice ? (
              <p className="text-2xl font-semibold text-primary mb-5">₹ {displayPrice.toLocaleString("en-IN")}</p>
            ) : null}

            {course.short_description ? (
              <p className="text-gray-700 text-lg leading-7 mb-5">{course.short_description}</p>
            ) : null}

            {features.length > 0 ? (
              <ul className="space-y-2 text-gray-700 mb-8">
                {features.map((feature, index) => (
                  <li key={`${feature}-${index}`}>• {feature}</li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-600 mb-8">Course details will be updated soon.</p>
            )}

            {batchDetails.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {batchDetails.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={`${item.label}-${item.value}`}
                      className="rounded-xl border border-[#dce8f8] bg-[#f8fbff] px-4 py-3"
                    >
                      <div className="flex items-start gap-3">
                        <Icon size={20} className="text-primary mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-500">{item.label}</p>
                          <p className="text-sm font-medium text-[#1A1A1A]">{item.value}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}

            <div className="mt-auto flex flex-wrap gap-3">
              <Link
                href={`/register/full?course_id=${courseId}`}
                className="px-5 py-3 rounded-md bg-primary text-white font-medium hover:opacity-90"
              >
                Buy Full Course
              </Link>
              <Link
                href="/"
                className="px-5 py-3 rounded-md border border-gray-300 text-gray-700 hover:border-primary hover:text-primary"
              >
                Back
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_0.7fr] gap-8 mt-8">
          <div className="bg-white border border-gray-100 rounded-2xl p-6 lg:p-8 shadow-sm">
            <h2 className="text-2xl font-semibold text-[#1A1A1A] mb-4">Course Overview</h2>
            {course.description ? (
              <p className="text-gray-700 leading-7 mb-8 whitespace-pre-line">{course.description}</p>
            ) : null}

            {curriculumSections.length > 0 ? (
              <>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-4">Course Content</h3>
              <div className="space-y-4">
                {curriculumSections.map((section, sectionIndex) => (
                  <div key={`${section.section_title}-${sectionIndex}`} className="rounded-xl border border-[#e8eef8]">
                    <div className="flex items-center justify-between px-4 py-3 bg-[#f8fbff] rounded-t-xl">
                      <h3 className="font-medium text-[#1A1A1A]">{section.section_title || `Module ${sectionIndex + 1}`}</h3>
                      <span className="text-sm text-gray-500">{section.items.length} items</span>
                    </div>
                    <ul className="px-4 py-4 space-y-3">
                      {section.items.map((item, itemIndex) => (
                        <li
                          key={`${item.title}-${itemIndex}`}
                          className="flex items-center justify-between gap-4 text-sm text-gray-700"
                        >
                          <span>{item.title}</span>
                          <span className="rounded-full bg-[#eef5ff] px-3 py-1 text-xs font-medium text-primary">
                            {item.type === "picture_as_pdf" ? "PDF" : "Video"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              </>
            ) : (
              <p className="text-gray-600">Course content will be updated soon.</p>
            )}
          </div>

          <div className="bg-white border border-gray-100 rounded-2xl p-6 lg:p-8 shadow-sm h-fit">
            <h2 className="text-2xl font-semibold text-[#1A1A1A] mb-4">Download Curriculum</h2>
            <p className="text-gray-600 mb-6">
              Download the latest curriculum documents available for this course.
            </p>

            {curriculumDocuments.length > 0 ? (
              <div className="space-y-3">
                {curriculumDocuments.map((document, index) => (
                  <a
                    key={`${document.title}-${index}`}
                    href={document.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-4 rounded-xl border border-[#dce8f8] px-4 py-3 hover:border-primary hover:bg-[#f8fbff]"
                  >
                    <div>
                      <p className="text-sm font-medium text-[#1A1A1A]">{document.title}</p>
                      {document.section_title ? (
                        <p className="text-xs text-gray-500">{document.section_title}</p>
                      ) : null}
                    </div>
                    <DownloadSimple size={18} className="text-primary shrink-0" />
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-gray-600">Curriculum PDFs are not available for this course yet.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CourseDetailClient;
